import isNull from 'lodash/isNull';
import { z } from 'zod';

import { FILTER_PUBLISHED_DEALERS } from '@/constants/dealers/dealers.constants';
import { CarsDealership } from '@/types/dealership/dealership.types';
import { DealershipReq } from '@/types/request/request.types';

import { directusService, zodKeys } from '../api.service';

export function mapDealershipResponse(
  dealership: DealershipReq,
): CarsDealership {
  return {
    id: dealership.id,
    main_image: dealership.main_image,
    name: dealership.name,
    main_number: dealership.main_number,
    city: dealership.city?.name,
    location: [
      dealership.address_1,
      dealership.address_2,
      dealership.city?.name,
      dealership.code,
    ]
      .filter((item) => item)
      .join(', '),
    google_address: dealership.google_address,
    enquiry_form_services: dealership?.enquiry_form_services,
    operating_hours: dealership.operating_hours
      .map((item) => {
        if (!item.operating_hours_id) {
          return {
            id: null,
            day_of_the_week: '',
            hours: '',
          };
        }

        const { id, day_of_the_week, closing_time, opening_time } =
          item.operating_hours_id as any;

        return {
          id: id % 7,
          day_of_the_week: day_of_the_week.slice(0, 3),
          hours: closing_time
            ? opening_time + ' - ' + closing_time
            : opening_time,
        };
      })
      .filter((item) => !isNull(item.id))
      .sort((a, b) => {
        const daysOfWeek = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

        return (
          daysOfWeek.indexOf(a.day_of_the_week) -
          daysOfWeek.indexOf(b.day_of_the_week)
        );
      }),
  };
}

const DealerSchema = z.object({
  id: z.string(),
  name: z.string(),
  address_1: z.string().nullable(),
  cms_new_dealer_code: z.string().nullable(),
  province: z
    .object({
      id: z.union([z.string(), z.number()]),
      name: z.string(),
    })
    .nullable(),
});

export type DealerWithProvince = z.infer<typeof DealerSchema>;
const DealersResponseSchema = z.array(DealerSchema);

export const getMinimalDealers = async () => {
  const { data } = await directusService.get('/items/dealerships', {
    params: {
      fields: zodKeys(DealerSchema),
      filter: FILTER_PUBLISHED_DEALERS,
      sort: 'name',
    },
  });

  const parsed = DealersResponseSchema.safeParse(data);

  if (!parsed.success) {
    console.error('Dealer API validation failed', parsed.error.format());
    throw new Error('Invalid dealer data format');
  }

  return parsed.data;
};
